import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const lgoCardVariants = cva(
  "rounded-lg border text-card-foreground transition-colors",
  {
    variants: {
      variant: {
        flat: "bg-card border-border",
        raised: "bg-card border-border shadow-sm",
        accent: "bg-accent text-accent-foreground border-transparent",
      },
      padding: {
        none: "",
        sm: "p-3",
        md: "p-5",
        lg: "p-6",
      },
    },
    defaultVariants: {
      variant: "flat",
      padding: "md",
    },
  },
);

export interface LGoCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof lgoCardVariants> {
  asChild?: boolean;
}

export const LGoCard = React.forwardRef<HTMLDivElement, LGoCardProps>(
  ({ className, variant, padding, ...props }, ref) => (
    <div ref={ref} className={cn(lgoCardVariants({ variant, padding }), className)} {...props} />
  ),
);
LGoCard.displayName = "LGoCard";

export const LGoCardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col gap-1.5 mb-4 last:mb-0", className)} {...props} />
  ),
);
LGoCardHeader.displayName = "LGoCardHeader";

export const LGoCardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("font-display text-lg font-semibold tracking-tight leading-tight", className)}
      {...props}
    />
  ),
);
LGoCardTitle.displayName = "LGoCardTitle";

export const LGoCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
LGoCardDescription.displayName = "LGoCardDescription";

export const LGoCardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("", className)} {...props} />,
);
LGoCardContent.displayName = "LGoCardContent";

export const LGoCardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex items-center justify-end gap-2 mt-4 pt-4 border-t border-border", className)}
      {...props}
    />
  ),
);
LGoCardFooter.displayName = "LGoCardFooter";

export { lgoCardVariants };
